import React, { useEffect, useState } from 'react';
import { IOrder } from '@/interfaces/order';
import { IBillingNote } from '@/interfaces/billing-note';
import { useSetting } from '@/contexts/CompanyContext';
import {
  formatThaiDate,
  formatNumber,
  convertNumberToThaiText,
} from '@/utils/dataUtils';
import { getFullAddress } from '@/utils/customerUtils';
import { mediaUrl } from '@/utils/mediaUtils';
import { Dayjs } from 'dayjs';

interface IProps {
  data: IOrder | IOrder[];
  billingNote?: IBillingNote;
  signature?: number;
}

interface IBillingRow {
  ivNo?: string;
  date?: Dayjs | string | Date;
  dueDate?: Dayjs | string | Date;
  amount: number;
  paidAmount?: number;
  balanceAmount?: number;
}

const MIN_ROWS = 10;

const TM31 = ({ data, billingNote }: IProps) => {
  const { setting } = useSetting();
  const [logo, setLogo] = useState<string>('');

  const orders: IOrder[] = Array.isArray(data) ? data : data ? [data] : [];
  const firstOrder = orders[0];

  useEffect(() => {
    const fetchLogoUrl = async () => {
      if (firstOrder?.businessUnitSignature) {
        const url = await mediaUrl({
          id: firstOrder.businessUnitSignature || 0,
        });
        setLogo(url || '');
      }
    };

    fetchLogoUrl();
  }, [firstOrder?.businessUnitSignature]);

  // build billing rows: from billing note items if provided, otherwise from orders
  const rows: IBillingRow[] = billingNote?.billingNoteItems?.length
    ? billingNote.billingNoteItems.map(item => {
        const amount = Number(item.amount || 0);
        const paidAmount = Number(item.paidAmount || 0);
        return {
          ivNo: item.ivNo || '',
          date: item.orderDate,
          dueDate: item.dueDate,
          amount,
          paidAmount,
          balanceAmount: amount - paidAmount,
        };
      })
    : orders.map(order => ({
        ivNo: order?.ivNo || '',
        date: order?.orderDate,
        dueDate: order?.dueDate,
        amount: Number(order?.summary || 0),
        paidAmount: 0,
        balanceAmount: Number(order?.summary || 0),
      }));

  const totalAmount = billingNote
    ? Number(billingNote.totalAmount || 0)
    : rows.reduce((sum, r) => sum + r.amount, 0);
  const totalPaid = rows.reduce((sum, r) => sum + Number(r.paidAmount || 0), 0);
  const totalBalance = rows.reduce(
    (sum, r) => sum + Number(r.balanceAmount || 0),
    0,
  );

  const cellBorder: React.CSSProperties = {
    border: '1px solid black',
  };

  const customer = billingNote?.customer || firstOrder?.customer;

  const billingCode =
    billingNote?.code ||
    (firstOrder?.code ? `BI${firstOrder.code.substring(2)}` : '-');

  const billingDate = billingNote?.docDate || firstOrder?.orderDate;

  const emptyRows = Math.max(0, MIN_ROWS - rows.length);

  return (
    <div
      style={{
        maxWidth: '209mm',
        minWidth: '209mm',
        minHeight: '296mm',
        maxHeight: '296mm',
        backgroundColor: 'white',
        margin: '0px auto',
        position: 'relative',
        padding: 20,
        pageBreakBefore: 'always',
      }}
    >
      {/* Header */}
      <div className="flex flex-col w-full mb-4">
        <div className="font-bold text-sm">{setting?.companyName || ''}</div>
        <div className="text-xs">{setting?.companyAddress || ''}</div>
        <div className="flex flex-row gap-3 text-xs">
          <div>Tel: {setting?.companyPhoneNumber || ''}</div>
          <div>Fax: {setting?.companyPhoneFax || ''}</div>
        </div>
        <div className="text-xs">
          เลขประจำตัวผู้เสียภาษี {setting?.companyTaxId || ''} (สำนักงานใหญ่)
        </div>
      </div>

      {/* Title */}
      <div className="flex justify-center mb-4">
        <div className="text-xl font-bold">ใบวางบิล</div>
      </div>

      {/* Customer Info & Document Info */}
      <div className="flex flex-row justify-between mb-2 text-xs">
        <div className="basis-1/2">
          <div className="flex flex-row gap-1 mb-1">
            <div className="min-w-[50px]">ลูกค้า</div>
            <div className="border-b border-black flex-1 min-h-[20px] px-1">
              {customer?.code || ''}
            </div>
          </div>
          <div className="flex flex-row gap-1 mb-1">
            <div className="min-w-[50px]">บริษัท</div>
            <div className="border-b border-black flex-1 min-h-[20px] px-1">
              {customer?.name || ''}
            </div>
          </div>
          <div className="flex flex-row gap-1 mb-1">
            <div className="border-b border-black flex-1 min-h-[20px] px-1">
              {getFullAddress(customer) || ''}
            </div>
          </div>
          <div className="flex flex-row gap-1 mb-1">
            <div className="border-b border-black flex-1 min-h-[20px] px-1">
              {`Tel: ${customer?.phoneNumber || '-'} , Tax ID: ${customer?.taxId || '-'} , Branch: ${customer?.branchId || '00000'}`}
            </div>
          </div>
        </div>
        <div className="basis-1/3">
          <div className="flex flex-row justify-between mb-1">
            <div className="font-bold">เลขที่ใบวางบิล</div>
            <div>{billingCode}</div>
          </div>
          <div className="flex flex-row justify-between mb-1">
            <div className="font-bold">วันที่</div>
            <div>{formatThaiDate(billingDate)}</div>
          </div>
        </div>
      </div>

      {/* Remark */}
      <div className="text-xs mb-2">
        <span className="font-bold">หมายเหตุ :</span>{' '}
        {billingNote?.remark || ''}
      </div>

      {/* Billing Items Table */}
      <div className="flex w-full text-xs mt-2">
        <table
          border={1}
          cellSpacing={0}
          cellPadding={5}
          style={{
            width: '100%',
            border: '0.5px black solid',
            borderCollapse: 'collapse',
            minHeight: 300,
          }}
        >
          <thead>
            <tr>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={40}
              >
                <strong>No.</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={120}
              >
                <strong>เลขที่ใบกำกับ</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={80}
              >
                <strong>วันที่</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={80}
              >
                <strong>ครบกำหนด</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={100}
              >
                <strong>จำนวนเงิน</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={100}
              >
                <strong>ชำระแล้ว</strong>
              </td>
              <td
                style={{
                  ...cellBorder,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
                width={100}
              >
                <strong>เงินคงค้าง</strong>
              </td>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`row-${index}`}>
                <td style={{ ...cellBorder, textAlign: 'center' }}>
                  {index + 1}
                </td>
                <td style={{ ...cellBorder, textAlign: 'center' }}>
                  {row?.ivNo || '-'}
                </td>
                <td style={{ ...cellBorder, textAlign: 'center' }}>
                  {formatThaiDate(row?.date)}
                </td>
                <td style={{ ...cellBorder, textAlign: 'center' }}>
                  {formatThaiDate(row?.dueDate)}
                </td>
                <td style={{ ...cellBorder, textAlign: 'right' }}>
                  {formatNumber(row?.amount || 0, 2)}
                </td>
                <td style={{ ...cellBorder, textAlign: 'right' }}>
                  {formatNumber(row?.paidAmount || 0, 2)}
                </td>
                <td style={{ ...cellBorder, textAlign: 'right' }}>
                  {formatNumber(row?.balanceAmount || 0, 2)}
                </td>
              </tr>
            ))}
            {/* Empty rows to fill space */}
            {Array.from({ length: emptyRows }).map((_, i) => (
              <tr key={`empty-${i}`}>
                <td style={cellBorder}>&nbsp;</td>
                <td style={cellBorder}></td>
                <td style={cellBorder}></td>
                <td style={cellBorder}></td>
                <td style={cellBorder}></td>
                <td style={cellBorder}></td>
                <td style={cellBorder}></td>
              </tr>
            ))}
            {/* Total row */}
            <tr>
              <td
                colSpan={4}
                style={{
                  ...cellBorder,
                  textAlign: 'left',
                }}
              >
                <div className="flex flex-row justify-between items-center">
                  <div>({convertNumberToThaiText(totalAmount)})</div>
                  <strong>รวมเงินทั้งสิ้น</strong>
                </div>
              </td>
              <td style={{ ...cellBorder, textAlign: 'right' }}>
                <strong>{formatNumber(totalAmount, 2)}</strong>
              </td>
              <td style={{ ...cellBorder, textAlign: 'right' }}>
                <strong>{formatNumber(totalPaid, 2)}</strong>
              </td>
              <td style={{ ...cellBorder, textAlign: 'right' }}>
                <strong>{formatNumber(totalBalance, 2)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 50,
          width: '100%',
          padding: '20px',
        }}
      >
        {/* Bank Info */}
        <strong>กรุณาชำระเงินโดย</strong>
        <div className="flex w-full text-xs mb-4">
          <table
            border={1}
            cellSpacing={0}
            cellPadding={5}
            style={{
              width: '100%',
              border: '0.5px; black solid',
              borderCollapse: 'collapse',
            }}
          >
            <tr>
              <td colSpan={2} style={{ border: '1px solid black' }}>
                <div>
                  กรุณาสั่งจ่ายเช็คขีดคร่อม <strong> (A/C PAYEE ONLY) </strong>
                  ในนาม
                  <strong> “{`${setting?.companyChequeNameTh || ''}`}”</strong>
                </div>
                <div>
                  Please make the cheque <strong> (A/C PAYEE ONLY) </strong>
                  payable to
                  <strong> “{`${setting?.companyChequeNameEn || ''}`}”</strong>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ border: '1px solid black' }}>
                <div className="flex gap-5">
                  <div>
                    <div>
                      <strong>ชำระผ่านช่องทางโอนเงิน (ทุกธนาคาร)</strong>
                    </div>
                    <div>
                      1. โอนเงินผ่านเข้าธนาคารกสิกรไทย&emsp;ชื่อบัญชี บริษัท
                      อัลไลด์ อโกร จำกัด&emsp;เลขที่บัญชี 228-1-23396-6
                    </div>
                    <div>
                      2. โอนเงินผ่านเข้าธนาคารกรุงเทพ
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ชื่อบัญชี บริษัท
                      อัลไลด์ อโกร จำกัด&emsp;เลขที่บัญชี 114-4-59884-2
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>

        {/* Signature Section */}
        <div className="flex flex-row justify-between text-xs">
          <div className="flex flex-col gap-1">
            <div>ชื่อผู้รับวางบิล ...........................</div>
            <div>วันที่รับ ......./......./........</div>
            <div>วันที่นัดรับเช็ค ......./......./........</div>
          </div>
          <div className="flex flex-col gap-1 items-end relative">
            <div>
              ในนาม {setting?.companyChequeNameTh || setting?.companyName || ''}
            </div>

            <div className="mt-2">
              ชื่อผู้วางบิล
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 150,
                  borderBottom: '1px solid black',
                  marginLeft: 4,
                }}
              >
                {logo && (
                  <div className="flex justify-center min-w-[100px] mt-2">
                    <img
                      src={logo}
                      alt="pioneerStamp"
                      style={{ maxHeight: 50 }}
                    />
                  </div>
                )}
                &nbsp;
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TM31;
